import type { AdminInspectorListItemDto } from '@carcheck/shared';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { shortId } from './displayName';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { initials } from '../../../utils/initials';
import { placeText } from '../../../utils/place';
import { InspectorKycStatusBadge, StatusBadge } from '../_components/InspectorBadges';

/**
 * Column definitions for the inspectors DataGrid (AUT-125), extracted from
 * the page so the page component stays under the line cap. Sorting is
 * disabled per column — the backend list is fixed `createdAt DESC` (no
 * arbitrary sort), so the grid never sends a sort the server can't honour.
 *
 * PII cells render through `<RedactedField perm="view_pii">`, agreeing
 * with `row.piiRevealed` (both derive from the same backend permission).
 * A revealed value is never re-redacted; only revealed values get a copy
 * button.
 *
 * Columns mirror the mockup `inspectors-list.html`: Инспектор (avatar +
 * name + id) / Телефон / Город / Статус / KYC / Отзывов (right-aligned
 * monospace) / Рейтинг (monospace + star). The KYC chip on the list is
 * DERIVED from the FSM `status` — the list DTO does not carry a separate
 * `kyc.sessionStatus` field; only the detail does. `InspectorKycStatusBadge`
 * maps the FSM to a coarse KYC label (e.g. `idcheck_passed`/`active` →
 * "ID пройден"). The "Reviews" column shows `row.reviewCount` (the list
 * does not carry an orders-completed count — that's a separate ticket).
 */

const InspectorCell = ({
  row,
  t,
}: {
  row: AdminInspectorListItemDto;
  t: TFunction;
}): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%' }}>
    <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700 }}>
      {initials(row.name)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {row.name ?? t('inspectors.detail.noValue')}
      </Typography>
      <Typography variant="caption" sx={{ color: tokens.text.tertiary }} fontFamily="monospace">
        {shortId(row.id)}
      </Typography>
    </Box>
  </Box>
);

const PiiCell = ({ value, revealed }: { value: string; revealed: boolean }): ReactElement => (
  <RedactedField
    perm="view_pii"
    value={value}
    redacted={value}
    {...(revealed ? { copyable: true } : {})}
  />
);

const RatingCell = ({ row }: { row: AdminInspectorListItemDto }): ReactElement => {
  // `0.00` (server's BigDecimal default) + zero reviews → render dash.
  if (row.reviewCount === 0) {
    return (
      <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
        —
      </Typography>
    );
  }
  return (
    <Typography variant="body2" fontFamily="monospace">
      {row.rating}
      <Box component="span" sx={{ ml: 0.5, color: 'warning.main' }} aria-label="rating-star">
        ★
      </Box>
    </Typography>
  );
};

export const buildInspectorColumns = (t: TFunction): GridColDef<AdminInspectorListItemDto>[] => [
  {
    field: 'inspector',
    headerName: t('inspectors.columns.inspector'),
    flex: 1.4,
    minWidth: 220,
    sortable: false,
    renderCell: ({ row }) => <InspectorCell row={row} t={t} />,
  },
  {
    field: 'phone',
    headerName: t('inspectors.columns.phone'),
    flex: 1,
    minWidth: 160,
    sortable: false,
    renderCell: ({ row }) => {
      const phone = row.phone ?? '';
      if (!phone) {
        return (
          <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
            {t('inspectors.detail.noValue')}
          </Typography>
        );
      }
      return <PiiCell value={phone} revealed={row.piiRevealed} />;
    },
  },
  {
    field: 'city',
    headerName: t('inspectors.columns.city'),
    flex: 0.7,
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }) => <Typography variant="body2">{placeText(row)}</Typography>,
  },
  {
    field: 'status',
    headerName: t('inspectors.columns.status'),
    flex: 0.8,
    minWidth: 130,
    sortable: false,
    renderCell: ({ row }) => <StatusBadge status={row.status} />,
  },
  {
    field: 'kyc',
    headerName: t('inspectors.columns.kyc'),
    flex: 0.8,
    minWidth: 130,
    sortable: false,
    renderCell: ({ row }) => <InspectorKycStatusBadge status={row.status} />,
  },
  {
    field: 'reviews',
    headerName: t('inspectors.columns.reviews'),
    flex: 0.5,
    minWidth: 100,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ row }) => (
      <Typography variant="body2" fontFamily="monospace">
        {row.reviewCount}
      </Typography>
    ),
  },
  {
    field: 'rating',
    headerName: t('inspectors.columns.rating'),
    flex: 0.6,
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }) => <RatingCell row={row} />,
  },
];
